/**
 * sl-728o.1.2: TutorialRegistry Service
 *
 * Central registry for tutorial sessions. Manages registration,
 * active tutorial state, and navigation through steps.
 */

import { LiveData, Service } from '@toeverything/infra';

import type { TutorialSession } from '../types';
import type { TutorialPersistence } from './tutorial-persistence';

interface ActiveTutorial {
  session: TutorialSession;
  step: number;
}

export class TutorialRegistry extends Service {
  private readonly sessions = new Map<string, TutorialSession>();

  readonly activeTutorial$ = new LiveData<ActiveTutorial | null>(null);

  constructor(private readonly persistence: TutorialPersistence) {
    super();
  }

  // --- Registration ---

  register(session: TutorialSession): void {
    this.sessions.set(session.id, session);
  }

  unregister(sessionId: string): void {
    // If currently active, skip it first
    const active = this.activeTutorial$.value;
    if (active && active.session.id === sessionId) {
      this.skip();
    }
    this.sessions.delete(sessionId);
  }

  get(sessionId: string): TutorialSession | undefined {
    return this.sessions.get(sessionId);
  }

  list(): TutorialSession[] {
    return Array.from(this.sessions.values());
  }

  listByContext(key: string, value: unknown): TutorialSession[] {
    return this.list().filter(s => s.context?.[key] === value);
  }

  // --- Tutorial Lifecycle ---

  /**
   * Start a tutorial session.
   * Returns false if session not found or prerequisites not met.
   */
  start(sessionId: string): boolean {
    const session = this.sessions.get(sessionId);
    if (!session) {
      return false;
    }

    // Check prerequisite
    if (
      session.prerequisite &&
      !this.persistence.isCompleted(session.prerequisite)
    ) {
      return false;
    }

    // Check external prerequisite
    if (session.externalPrerequisite && !session.externalPrerequisite()) {
      return false;
    }

    // If another tutorial is active, skip it first
    if (this.activeTutorial$.value) {
      this.skip();
    }

    this.activeTutorial$.next({ session, step: 0 });
    return true;
  }

  next(): void {
    const active = this.activeTutorial$.value;
    if (!active) return;

    const nextStep = active.step + 1;
    if (nextStep >= active.session.steps.length) {
      this.complete();
    } else {
      this.activeTutorial$.next({ ...active, step: nextStep });
    }
  }

  prev(): void {
    const active = this.activeTutorial$.value;
    if (!active || active.step <= 0) return;

    this.activeTutorial$.next({ ...active, step: active.step - 1 });
  }

  /**
   * Skip the current tutorial — marks as completed.
   */
  skip(): void {
    this.endTutorial('completed');
  }

  /**
   * Complete the current tutorial — marks as completed.
   */
  complete(): void {
    this.endTutorial('completed');
  }

  /**
   * Dismiss the current tutorial — marks as deferred (not completed).
   * Tutorial will re-appear on next visit.
   */
  dismiss(): void {
    this.endTutorial('deferred');
  }

  // --- Private ---

  private endTutorial(state: 'completed' | 'deferred'): void {
    const active = this.activeTutorial$.value;
    if (!active) return;

    if (state === 'completed') {
      this.persistence.markCompleted(active.session.id);
      this.runLifecycleCallback(active.session.onComplete);
    } else {
      this.persistence.markDeferred(active.session.id);
      this.runLifecycleCallback(active.session.onDismiss);
    }
    this.activeTutorial$.next(null);
  }

  private runLifecycleCallback(
    callback: (() => void | Promise<void>) | undefined
  ): void {
    if (!callback) return;
    void Promise.resolve(callback()).catch(error => {
      console.error('Tutorial lifecycle callback failed', error);
    });
  }
}
